import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Check, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import CTASection from "@/components/CTASection";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { DETAILED_SERVICES, ENGAGEMENT_MODELS } from "@/data/content";

const Block = ({ label, children }) => (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt">{label}</p>
    <div className="mt-4">{children}</div>
  </div>
);

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="QA Testing Services — Manual, Regression, API, Web & Mobile | Avirova Technologies"
        description="Outsourced QA services: manual testing, functional and regression testing, web and mobile application testing, API testing, UAT support and QA consulting for startups and SaaS companies."
      />

      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-24" data-testid="services-hero">
        <div className="grid-lines absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
              {"// Services"}
            </p>
          </MaskedLine>
          <h1 className="mt-6 max-w-4xl font-display text-5xl font-black leading-[0.98] tracking-tighter text-zinc-100 sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.2}>QA services designed</MaskedLine>
            <MaskedLine delay={0.32}>
              around your <span className="text-volt">product.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.46}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              From a one-time testing assignment to ongoing outsourced QA
              support, choose the level of testing your product needs.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8" data-testid="services-list">
        {DETAILED_SERVICES.map((s, i) => (
          <div
            key={s.slug}
            id={s.slug}
            data-testid={`service-detail-${s.slug}`}
            className="grid scroll-mt-24 gap-10 border-t border-zinc-800/80 py-16 lg:grid-cols-12 lg:py-20"
          >
            <div className="lg:col-span-4">
              <Reveal>
                <span className="font-mono text-sm text-volt">{s.num} //</span>
                <h2 className="mt-4 flex flex-wrap items-center gap-3 font-display text-3xl font-extrabold tracking-tighter text-zinc-100 sm:text-4xl">
                  {s.title}
                  {s.comingSoon && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-volt/30 bg-volt/[0.07] px-3 py-1 font-mono text-[10px] font-normal uppercase tracking-[0.18em] text-volt">
                      <Clock className="h-3 w-3" /> Coming Soon
                    </span>
                  )}
                </h2>
              </Reveal>
            </div>
            <div className="grid gap-10 lg:col-span-8 lg:grid-cols-3">
              <Reveal delay={0.08}>
                <Block label="What it includes">
                  <ul className="space-y-2.5">
                    {s.includes.map((item) => (
                      <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-zinc-400">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-volt/70" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Block>
              </Reveal>
              <Reveal delay={0.14}>
                <Block label="When you need it">
                  <p className="text-sm leading-relaxed text-zinc-400">{s.need}</p>
                </Block>
              </Reveal>
              <Reveal delay={0.2}>
                <Block label="What you receive">
                  <ul className="space-y-2.5">
                    {s.receive.map((item) => (
                      <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-zinc-400">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-volt/70" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Block>
              </Reveal>
            </div>
          </div>
        ))}
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="border-t border-zinc-800/80" data-testid="engagement-models-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHeader
            overline="Engagement"
            title="Flexible engagement models"
            copy="No fixed price lists — scope is shaped around your product, release cycle and risk. Tell us what you're shipping and we'll propose the right setup."
          />
          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {ENGAGEMENT_MODELS.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`engagement-model-${m.num}`}
                  className="group flex h-full flex-col rounded-2xl border border-zinc-800 bg-ink-900/60 p-8 transition-colors duration-500 hover:border-volt/40"
                >
                  <span className="font-mono text-sm text-volt">{m.num} //</span>
                  <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-zinc-100">
                    {m.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="engagement-cta-button"
              className="group mt-12 inline-flex items-center gap-2 rounded-full border border-zinc-700 px-7 py-3.5 text-sm font-semibold text-zinc-200 transition-colors duration-300 hover:border-volt/50 hover:text-volt"
            >
              Discuss Your Requirements
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
